import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials, setLoading, setError, clearError, logout } from '../features/authSlice';
import { authAPI } from '../services/authApi';
import { materialsApi } from '../services/materialsApi';
import { bookmarksApi } from '../services/bookmarksApi';
import styles from '../css/Login.module.css';

const invalidateAllCaches = (dispatch) => {
  dispatch(materialsApi.util.invalidateTags(['Materials']));
  dispatch(bookmarksApi.util.invalidateTags(['Bookmarks']));
};

const AvatarWithSkeleton = ({ avatarUrl }) => {
  const [loaded, setLoaded] = useState(false);

  if (!avatarUrl) {
    return (
      <div className={styles.avatarWrap}>
        <div className={styles.avatarPlaceholder}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.avatarWrap}>
      {!loaded && <div className={styles.avatarSkeleton} />}
      <img
        src={avatarUrl}
        alt="avatar"
        className={styles.avatar}
        style={loaded ? undefined : { display: 'none' }}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
};

const TELEGRAM_BOT_NAME = 'DoramaHypeOAuthBot';

// Страница /auth/login:
// - без токена и без auth → показывает кнопки Google + Telegram
// - с ?access_token= → загружает профиль → показывает подтверждение
// - уже аутентифицирован → показывает подтверждение
const AuthLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error, isAuthenticated, user } = useSelector((state) => state.auth);
  const tgWidgetRef = useRef(null);

  const handleOAuthCallback = useCallback(async (token) => {
    dispatch(setLoading(true));
    try {
      const response = await authAPI.getMe(token);
      const userData = response.data.data;
      dispatch(setCredentials({ user: userData, accessToken: token }));
      invalidateAllCaches(dispatch);
    } catch {
      dispatch(setError('Не удалось получить данные пользователя'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(clearError());
    const token = searchParams.get('access_token');
    const oauthError = searchParams.get('error');

    if (oauthError) {
      const errorMessages = {
        invalid_state: 'Ошибка безопасности. Попробуйте ещё раз.',
        missing_code: 'Авторизация отменена.',
        google_failed: 'Не удалось войти через Google.',
        auth_failed: 'Ошибка авторизации.',
        token_failed: 'Ошибка создания сессии.',
        banned: 'Аккаунт заблокирован.',
      };
      dispatch(setError(errorMessages[oauthError] || 'Произошла ошибка авторизации'));
      return;
    }

    if (token) {
      handleOAuthCallback(token);
    }
  }, [searchParams, dispatch, handleOAuthCallback]);

  // Telegram виджет — вставляем скрипт когда показываем кнопки
  useEffect(() => {
    if (isAuthenticated || loading) return;
    const token = searchParams.get('access_token');
    const oauthError = searchParams.get('error');
    if (token || oauthError) return;

    const container = tgWidgetRef.current;
    if (!container) return;

    window.onTelegramAuth = async (data) => {
      dispatch(setLoading(true));
      dispatch(clearError());
      try {
        const res = await authAPI.telegramLogin(data);
        const payload = res.data.data;
        if (payload.is_new) {
          navigate(`/auth/register?pending_token=${payload.pending_token}`, { replace: true });
        } else {
          const meRes = await authAPI.getMe(payload.access_token);
          dispatch(setCredentials({ user: meRes.data.data, accessToken: payload.access_token }));
          invalidateAllCaches(dispatch);
        }
      } catch {
        dispatch(setError('Не удалось войти через Telegram'));
      }
    };

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', TELEGRAM_BOT_NAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    container.innerHTML = '';
    container.appendChild(script);

    return () => {
      delete window.onTelegramAuth;
    };
  }, [isAuthenticated, loading, searchParams, dispatch, navigate]);

  const handleGoogleLogin = () => {
    window.location.href = authAPI.getGoogleLoginUrl();
  };

  const handleContinue = () => {
    navigate('/', { replace: true });
  };

  const handleSwitchAccount = async () => {
    try { await authAPI.logout(); } catch {}
    dispatch(logout());
    // Сбрасываем URL параметры чтобы показать кнопки
    navigate('/auth/login', { replace: true });
  };

  const showButtons = !isAuthenticated && !loading && !searchParams.get('access_token') && !searchParams.get('error');
  const showConfirm = isAuthenticated && user;

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginCard}>
        <div className={styles.logoBlock}>
          <span className={styles.logoText}>DoramaHype</span>
        </div>

        {/* Кнопки входа */}
        {showButtons && (
          <>
            <h1 className={styles.title}>Войти</h1>
            <p className={styles.subtitle}>Выберите способ входа</p>
            <div className={styles.socialButtons}>
              <button className={styles.socialButton} onClick={handleGoogleLogin}>
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Войти через Google
              </button>

              <div ref={tgWidgetRef} className={styles.tgWidgetHidden} />
            </div>
          </>
        )}

        {/* Ошибка */}
        {error && (
          <div className={styles.errorMessage}>
            {error}
            <div className={styles.errorActions}>
              <button className={styles.retryButton} onClick={() => navigate('/auth/login', { replace: true })}>
                Попробовать снова
              </button>
            </div>
          </div>
        )}

        {/* Загрузка */}
        {loading && (
          <div className={styles.loadingMessage}>
            Загружаем ваш профиль...
          </div>
        )}

        {/* Подтверждение профиля */}
        {showConfirm && (
          <div className={styles.profileConfirm}>
            <AvatarWithSkeleton avatarUrl={user.avatar_url} />

            <h2 className={styles.profileLogin}>
              {user.login || user.email || 'Пользователь'}
            </h2>

            {user.email && (
              <p className={styles.profileEmail}>{user.email}</p>
            )}

            <button className={styles.continueButton} onClick={handleContinue}>
              Продолжить
            </button>

            <button className={styles.switchAccount} onClick={handleSwitchAccount}>
              Войти в другой аккаунт
            </button>
          </div>
        )}

        {/* Промежуточное состояние: токен есть но профиль ещё не загружен */}
        {!showButtons && !showConfirm && !loading && !error && (
          <div className={styles.loadingMessage}>
            Подождите...
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthLogin;
